// Tier 1 Progress dashboard math helpers (#1–#4). All pure functions
// over already-fetched data — no I/O, no clock reads (except where a
// `nowMs` parameter is passed in). See docs/progress-redesign.md.
import { DAY_MS, WEEK_MS } from "./freshness.js";
import type { Exercise } from "./schemas/exercise.js";
import { MovementPattern } from "./schemas/muscle.js";
import type { TimedSessionResult } from "./progressTypes.js";
import { catalogBySlug } from "./volumeByMuscle.js";
import { EFFORT_SUBMAXIMAL_RIR } from "./effortScale.js";
import { MIN_TREND_POINTS } from "./trendAdmissibility.js";

// Window helper. Sessions from `nowMs - windowDays * DAY_MS` up to `nowMs`:
// a report over a past period passes its end as `nowMs` (PG-64).
const inWindow = (
  s: TimedSessionResult,
  nowMs: number,
  windowDays: number,
): boolean =>
  s.startedAtMs >= nowMs - windowDays * DAY_MS && s.startedAtMs <= nowMs;

// =========================================================================
// Tier 1 #1 — Volume per movement pattern.
// =========================================================================
// Sums sets + volume across every ExerciseResult in `sessions`,
// distributed to its Exercise.movementPattern via `catalog`. Returns
// zero entries for patterns that didn't appear in the window so the UI
// can render a complete ring chart without holes.

// `hardSets` is the effort-filtered subset of `sets` — sets trained at
// RIR ≤ EFFORT_HARD_SET_RIR (the hypertrophy accumulator unit, AP-2).
// Summed from ExerciseResult.hardSetCount, which the SessionResult compute
// derives from per-set RIR (the aggregate has no per-set effort otherwise).
export type PatternVolume = { sets: number; volumeKg: number; hardSets: number };

export const volumeByPattern = (
  sessions: TimedSessionResult[],
  catalog: ReadonlyArray<Exercise>,
  nowMs: number,
  // PG-1 period selector: windowDays is now caller-chosen (7/14/30/90/…), not
  // the old fixed 7|28. The window math already treats it as a plain number;
  // widening the type keeps every existing 7/28 callsite valid.
  windowDays: number,
): Record<MovementPattern, PatternVolume> => {
  const bySlug = catalogBySlug(catalog);
  // Keys come from the zod enum itself, never from a list retyped here. A
  // hand-written one silently kept the old world: `mobility` was added to
  // MovementPattern on 2026-09-10 and not to the list, `Object.fromEntries`
  // needs a cast that switched off the exhaustiveness check TS would have
  // failed on, and the bucket lookup below then read `undefined` — a fatal
  // TypeError on every Progress tab open (CRASH-1). Ordering is irrelevant:
  // computeMovementBalance sorts by sets.
  const acc: Record<MovementPattern, PatternVolume> = Object.fromEntries(
    MovementPattern.options.map((p) => [
      p,
      { sets: 0, volumeKg: 0, hardSets: 0 },
    ]),
  ) as Record<MovementPattern, PatternVolume>;
  for (const s of sessions) {
    if (!inWindow(s, nowMs, windowDays)) continue;
    for (const er of s.result.exerciseResults) {
      const pattern = bySlug.get(er.exerciseSlug)?.movementPattern ?? "other";
      const bucket = acc[pattern];
      bucket.sets += er.setCount;
      bucket.volumeKg += er.totalVolumeKg;
      // Legacy rows (pre-hardSetCount) parse the field as 0 via the schema
      // default, so this is safe; `?? 0` guards raw objects in tests too.
      bucket.hardSets += er.hardSetCount ?? 0;
    }
  }
  return acc;
};

// =========================================================================
// Tier 1 #2 — E1RM regression detection.
// =========================================================================
// Linear least-squares fit over the points in the trailing N-week
// window. Slope is kg/week (sign-bearing). R² gives the fit confidence;
// noisy data (R² < 0.2) is downgraded to "plateau" regardless of slope
// so a single outlier can't trigger a regression alert.

export type RegressionResult = {
  slopePerWeek: number;
  rSquared: number;
  pctChange: number; // signed; over the windowed segment
  flag: "regressing" | "plateau" | "progressing";
  pointCount: number;
};

// A drop in e1RM is only a true regression when the recent work was near-
// maximal. Two ways the slope gets downgraded "regressing" → "plateau":
//   (1) RELATIVE — recent half trained ≥ this many RIR further from failure
//       than the baseline half (a clear back-off vs how it used to be lifted).
export const EFFORT_REGRESSION_RIR_DROP = 1.5;
//   (2) ABSOLUTE — recent half is itself clearly submaximal (mean RIR ≥ this):
//       a set with reps left in reserve UNDER-states capacity, so its e1RM
//       can't prove a lower ceiling (same asymmetry as the MAX-lower path).
//       This catches the real leg-press case where the OLD sessions carry no
//       RIR at all (so the relative test can't fire) but the recent block is
//       explicitly RIR 3 (Antenore 2026-06-26).
// Canonical definition now lives in effortScale.ts (shared with the AP-2
// hard-set accumulator); re-exported here for existing importers.
export { EFFORT_SUBMAXIMAL_RIR };

// Mean RIR over the points that actually carry one; null when none do
// (legacy points before topSetRir, or sets logged without RIR) → the
// effort override stays inactive and the slope-only verdict stands.
const meanRir = (
  pts: ReadonlyArray<{ rir?: number | null }>,
): number | null => {
  let sum = 0;
  let count = 0;
  for (const p of pts) {
    if (p.rir != null) {
      sum += p.rir;
      count += 1;
    }
  }
  return count > 0 ? sum / count : null;
};

export const detectRegression = (
  // `rir` (RIR of the top-e1RM set) is optional: callers that supply it
  // get effort-aware downgrading; those that don't keep the slope-only
  // behaviour (back-compat).
  points: ReadonlyArray<{ atMs: number; kgPerRep1: number; rir?: number | null }>,
  nowMs: number,
  // PG-1 period selector: was 8|12; now caller-chosen (windowDays/7). The
  // slope fit already returns "plateau" below MIN_TREND_POINTS, so short
  // windows degrade gracefully instead of throwing.
  windowWeeks: number,
): RegressionResult => {
  const windowMs = windowWeeks * WEEK_MS;
  const cutoff = nowMs - windowMs;
  const filtered = points
    .filter((p) => p.atMs >= cutoff && p.kgPerRep1 > 0)
    .sort((a, b) => a.atMs - b.atMs);
  if (filtered.length < MIN_TREND_POINTS) {
    return {
      slopePerWeek: 0,
      rSquared: 0,
      pctChange: 0,
      flag: "plateau",
      pointCount: filtered.length,
    };
  }
  const n = filtered.length;
  const xs = filtered.map((p) => (p.atMs - filtered[0]!.atMs) / WEEK_MS);
  const ys = filtered.map((p) => p.kgPerRep1);
  const xMean = xs.reduce((a, b) => a + b, 0) / n;
  const yMean = ys.reduce((a, b) => a + b, 0) / n;
  let num = 0;
  let den = 0;
  for (let i = 0; i < n; i++) {
    num += (xs[i]! - xMean) * (ys[i]! - yMean);
    den += (xs[i]! - xMean) ** 2;
  }
  const slope = den === 0 ? 0 : num / den;
  const intercept = yMean - slope * xMean;
  let ssRes = 0;
  let ssTot = 0;
  for (let i = 0; i < n; i++) {
    const yi = ys[i]!;
    const yhat = intercept + slope * xs[i]!;
    ssRes += (yi - yhat) ** 2;
    ssTot += (yi - yMean) ** 2;
  }
  const rSquared = ssTot === 0 ? 1 : Math.max(0, 1 - ssRes / ssTot);
  const first = ys[0]!;
  const last = ys[n - 1]!;
  const pctChange = first === 0 ? 0 : (last - first) / first;
  // R² < 0.2 = too noisy to trust the slope; force plateau so a single
  // spike doesn't pop a regression alert.
  let flag: RegressionResult["flag"];
  if (rSquared < 0.2) {
    flag = "plateau";
  } else if (pctChange < -0.03) {
    flag = "regressing";
  } else if (pctChange > 0.03) {
    flag = "progressing";
  } else {
    flag = "plateau";
  }
  // Effort-aware override: a "regressing" slope is only trustworthy when
  // the recent work was near-maximal. Downgrade to "plateau" when the
  // recent half was either (1) clearly submaximal on its own (mean RIR ≥
  // EFFORT_SUBMAXIMAL_RIR — its e1RM understates capacity, can't prove a
  // lower ceiling), or (2) trained ≥ EFFORT_REGRESSION_RIR_DROP RIR lighter
  // than the baseline half. Needs RIR on the relevant side; absent ⇒ the
  // slope verdict stands (back-compat with pre-topSetRir points).
  if (flag === "regressing") {
    const mid = Math.floor(n / 2);
    const baselineRir = meanRir(filtered.slice(0, mid));
    const recentRir = meanRir(filtered.slice(mid));
    const recentSubmaximal =
      recentRir !== null && recentRir >= EFFORT_SUBMAXIMAL_RIR;
    const lighterThanBaseline =
      baselineRir !== null &&
      recentRir !== null &&
      recentRir - baselineRir >= EFFORT_REGRESSION_RIR_DROP;
    if (recentSubmaximal || lighterThanBaseline) {
      flag = "plateau";
    }
  }
  return { slopePerWeek: slope, rSquared, pctChange, flag, pointCount: n };
};
